/**
 * @component ry-toast
 * @category  feedback
 * @summary   Transient notification toast (Fomantic UI `.ui.toast` equivalent). Use the static `RyToast.success/error/info/warning(message)` API for one-line programmatic toasts, or place `<ry-toast variant="...">message</ry-toast>` directly in markup for declarative use. Auto-dismisses after `duration` ms (default 4000); pass `duration="0"` to make the toast sticky.
 *
 * Reference: Fomantic UI's toast module. ry-toast emphasizes the programmatic
 * API path — Fomantic's behavior is collapsed into static factories named
 * after each variant. The declarative element is still supported but rarely
 * used directly.
 *
 * @attribute variant     'info' | 'success' | 'warning' | 'error' ?   default="info"   Visual variant + the icon shown in the leading slot.
 * @attribute duration    number?   default=4000   Milliseconds before auto-dismiss. `0` disables auto-dismiss (the user must click the close button or call `el.dismiss()`).
 * @attribute closable    boolean   default=true   When `false`, the close button is not rendered. Set this together with `duration="0"` only if you control the lifecycle externally — otherwise the toast becomes undismissable.
 *
 * @property  state       'visible' | 'hiding'    Lifecycle state. Toasts start at empty state, transition to "visible" on the next frame, then "hiding" before removal.
 *
 * @method    dismiss()   Begin the close transition. Cancels any pending auto-dismiss timer. State transitions to "hiding"; the element is removed from DOM ~300ms later. Fires `ry:close` immediately.
 *
 * @method    RyToast.show(options: { message, variant?, duration? })   Create and append a new toast. Returns the toast element so callers can dismiss it programmatically.
 * @method    RyToast.success(message, duration?)   Convenience wrapper around `RyToast.show({ variant: 'success', ... })`.
 * @method    RyToast.error(message, duration?)     Convenience wrapper.
 * @method    RyToast.info(message, duration?)      Convenience wrapper.
 * @method    RyToast.warning(message, duration?)   Convenience wrapper.
 *
 * @event ry:beforeclose
 *        detail: {}
 *        fires:  at the top of `dismiss()` — close-button click, the auto-dismiss timer, or `el.dismiss()` — before the timer is cleared or any state change
 *        cancelable: preventDefault() keeps the toast on screen; `ry:close` does not fire. A vetoed auto-dismiss simply leaves the toast in place (it stays until the next dismiss() attempt).
 *
 * @event ry:close
 *        detail: {}
 *        fires:  immediately at the start of dismiss() — BEFORE the toast is removed from DOM. Listen on the element returned from `RyToast.show()` if you want to know when a programmatic toast goes away.
 *
 * @keyboard  None on the toast itself. The internal close button is a `<button>` and inherits native Enter/Space activation.
 *
 * @state     `data-ry-state`: empty (just inserted), `"visible"` (slid into view), `"hiding"` (closing). CSS reads state to drive the slide-in / slide-out.
 *
 * @content   `<ry-toast>` accepts a plain text message as its initial textContent (it's transformed into the content slot). For richer content, set innerHTML before calling show() — but most consumers use the static factories with plain strings.
 *
 * @dom
 *   [data-ry-target="toast-container"]   (singleton on document.body)
 *     > ry-toast[variant][data-ry-state="visible|hiding"]
 *       > div.ry-toast__icon       [data-ry-target="icon"]      {variant glyph}
 *       > div.ry-toast__content    [data-ry-target="content"]   {message}
 *       > button.ry-toast__close   [data-ry-target="close"] [close]   (omitted when closable="false")
 *
 * @gotcha  Static API auto-creates a singleton container on `<body>` the first time a toast appears. There is no way to use multiple containers. If your layout needs corner-specific stacks, you must implement them outside this component.
 *
 * @gotcha  `closable="false"` + `duration="0"` produces a sticky undismissable toast. The user cannot remove it without code intervention. Use this only when you intend to dismiss programmatically (e.g. for in-progress operation banners).
 *
 * @gotcha  `dismiss()` fires `ry:close` immediately but waits ~300ms before removing the element. Listeners can rely on the event firing first; tests should wait if they need to assert removal.
 *
 * @gotcha  The initial textContent is consumed and replaced — after setup() the original text is gone. Use `el.querySelector('[data-ry-target="content"]').textContent` to read the rendered message.
 *
 * @example declarative
 * ```html
 * <ry-toast variant="success">Saved successfully!</ry-toast>
 * ```
 *
 * @example all-variants
 * ```html
 * <ry-toast variant="info">Info message</ry-toast>
 * <ry-toast variant="success">Saved!</ry-toast>
 * <ry-toast variant="warning">Heads up</ry-toast>
 * <ry-toast variant="error">Something failed</ry-toast>
 * ```
 *
 * @example sticky-no-auto-dismiss
 * ```html
 * <ry-toast duration="0" variant="warning">Sticky — click × to dismiss</ry-toast>
 * ```
 *
 * @example non-closable
 * ```html
 * <ry-toast closable="false" duration="2000">Auto-only dismissal</ry-toast>
 * ```
 *
 * @example programmatic
 * ```html
 * <!-- Pair buttons with onclick handlers so the kitchen sink can demo it without inline scripts.
 *      In real code you'd import { RyToast } from '@ryanhelsing/ry-ui'. -->
 * <ry-button onclick="RyToast.success('Saved!')">Success</ry-button>
 * <ry-button onclick="RyToast.error('Network failed', 10000)">Error</ry-button>
 * <ry-button onclick="RyToast.warning('Heads up')">Warning</ry-button>
 * <ry-button onclick="RyToast.info('FYI')">Info</ry-button>
 * ```
 *
 * @tutorial declarative-mount-and-content
 * ```js
 * // Mounting an inline <ry-toast> transforms its textContent into the
 * // content slot, prepends an icon, and appends a close button.
 * const el = mount('<ry-toast variant="success" duration="0">Saved!</ry-toast>');
 * const content = el.querySelector('[data-ry-target="content"]');
 * await expectEqual(content.textContent.trim(), 'Saved!', 'message lifted into content slot');
 * await expectEqual(!!el.querySelector('[data-ry-target="icon"]'), true, 'icon slot present');
 * await expectEqual(!!el.querySelector('[data-ry-target="close"]'), true, 'close button present by default');
 * ```
 *
 * @tutorial close-button-dismisses
 * ```js
 * const el = mount('<ry-toast variant="info" duration="0">Hi</ry-toast>');
 * const closeBtn = el.querySelector('[data-ry-target="close"]');
 * await expectEvent(el, 'ry:close', () => closeBtn.click());
 * await expectAttr(el, 'data-ry-state', 'hiding', 'state transitions to hiding');
 * ```
 *
 * @tutorial programmatic-dismiss
 * ```js
 * // Wrong assumption: dismiss() removes the element synchronously. It
 * // does NOT — ry:close fires first, then the element is removed ~300ms
 * // later. Tests asserting removal must wait.
 * const el = mount('<ry-toast duration="0">Hi</ry-toast>');
 * const fired = [];
 * el.addEventListener('ry:close', () => fired.push({
 *   attached: el.isConnected,
 *   state: el.getAttribute('data-ry-state'),
 * }));
 * el.dismiss();
 * await expectEqual(fired.length, 1, 'ry:close fires');
 * await expectEqual(fired[0].attached, true, 'element still attached when ry:close fires');
 * await expectEqual(fired[0].state, 'hiding', 'state is "hiding" when ry:close fires');
 * ```
 *
 * @tutorial duration-zero-disables-auto-dismiss
 * ```js
 * // Wrong assumption: every toast self-dismisses. duration="0" keeps it
 * // sticky — only an explicit close button click or dismiss() call removes it.
 * const el = mount('<ry-toast duration="0">Sticky</ry-toast>');
 * await sleep(50);
 * await expectEqual(el.getAttribute('data-ry-state'), 'visible', 'still visible after time');
 * ```
 *
 * @tutorial auto-dismiss-after-duration
 * ```js
 * // Short duration so the test stays fast — confirm the auto-dismiss fires.
 * const el = mount('<ry-toast duration="40">Quick</ry-toast>');
 * const fired = [];
 * el.addEventListener('ry:close', () => fired.push(1));
 * await sleep(80);
 * await expectEqual(fired.length, 1, 'auto-dismiss fired ry:close');
 * await expectAttr(el, 'data-ry-state', 'hiding');
 * ```
 *
 * @tutorial beforeclose-veto
 * ```js
 * // preventDefault() on ry:beforeclose keeps the toast on screen, whatever
 * // asked it to close (close button, auto-dismiss timer, el.dismiss()).
 * const el = mount('<ry-toast duration="0">Hi</ry-toast>');
 * await sleep(20);
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.dismiss();
 * await expectEqual(el.getAttribute('data-ry-state'), 'visible', 'vetoed dismiss leaves the toast visible');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.dismiss();
 * await expectEqual(el.getAttribute('data-ry-state'), 'hiding', 'without the veto, dismiss() works');
 * await expectEqual(closed.length, 1, 'ry:close fired once');
 * ```
 *
 * @tutorial closable-false-removes-close-button
 * ```js
 * const el = mount('<ry-toast closable="false" duration="0">Hi</ry-toast>');
 * await expectEqual(el.querySelector('[data-ry-target="close"]'), null, 'no close button when closable=false');
 * ```
 *
 * @tutorial static-show-api
 * ```js
 * // Module-level access via window.RyToast (the bundle exports it).
 * // For this test we invoke via the constructor's static method directly.
 * const ctor = customElements.get('ry-toast');
 * const toast = ctor.success('Hello', 0);  // duration 0 to stay visible
 * await expectEqual(toast.tagName.toLowerCase(), 'ry-toast');
 * await expectAttr(toast, 'variant', 'success');
 * await expectEqual(toast.isConnected, true, 'inserted into the toast container');
 * await expectEqual(toast.parentElement?.getAttribute('data-ry-target'), 'toast-container');
 * toast.dismiss();
 * ```
 *
 * @see ry-alert
 * @see ry-tooltip
 */

import { RyElement } from '../core/ry-element.js';
import type { ToastVariant, ToastOptions } from '../types.js';

const DEFAULT_DURATION = 4000;

export class RyToast extends RyElement {
  #timeout: ReturnType<typeof setTimeout> | null = null;

  static observedAttributes = ['variant', 'duration'] as const;

  // Static container for programmatic toasts
  static #container: HTMLElement | null = null;

  static #getContainer(): HTMLElement {
    if (!RyToast.#container) {
      RyToast.#container = document.createElement('div');
      RyToast.#container.setAttribute('data-ry-target', 'toast-container');
      RyToast.#container.className = 'ry-toast-container'; // Keep for theme CSS
      document.body.appendChild(RyToast.#container);
    }
    return RyToast.#container;
  }

  // Static API
  static show(options: ToastOptions): RyToast {
    const toast = document.createElement('ry-toast') as RyToast;
    toast.textContent = options.message;

    if (options.variant) {
      toast.setAttribute('variant', options.variant);
    }
    if (options.duration !== undefined) {
      toast.setAttribute('duration', String(options.duration));
    }

    RyToast.#getContainer().appendChild(toast);
    return toast;
  }

  static success(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'success', duration });
  }

  static error(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'error', duration });
  }

  static info(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'info', duration });
  }

  static warning(message: string, duration?: number): RyToast {
    return RyToast.show({ message, variant: 'warning', duration });
  }

  setup(): void {
    this.#transformMarkup();

    // Close button (use [close] attribute)
    const closeBtn = this.$('[close]');
    if (closeBtn) {
      this.on(closeBtn, 'click', () => this.dismiss());
    }

    // Auto-dismiss
    const duration = parseInt(this.getAttribute('duration') ?? String(DEFAULT_DURATION), 10);
    if (duration > 0) {
      this.#timeout = setTimeout(() => this.dismiss(), duration);
    }

    // Animate in (CSS expects 'visible' state)
    requestAnimationFrame(() => {
      this.state = 'visible';
    });
  }

  #transformMarkup(): void {
    // Check if already transformed
    if (this.$('[data-ry-target="content"]')) return;

    const variant = this.getAttribute('variant') ?? 'info';
    const message = this.textContent?.trim() ?? '';
    const closable = this.getAttribute('closable') !== 'false';

    const icon = this.#getIcon(variant as ToastVariant);
    const closeBtn = closable
      ? `<button data-ry-target="close" class="ry-toast__close" close aria-label="Dismiss">&times;</button>`
      : '';

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <div data-ry-target="icon" class="ry-toast__icon">${icon}</div>
      <div data-ry-target="content" class="ry-toast__content">${message}</div>
      ${closeBtn}
    `;
  }

  #getIcon(variant: ToastVariant): string {
    const icons: Record<ToastVariant, string> = {
      info: 'ℹ',
      success: '✓',
      warning: '⚠',
      error: '✕'
    };
    return icons[variant] ?? icons.info;
  }

  dismiss(): void {
    if (!this.emit('beforeclose')) return;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.state = 'hiding';

    // Remove after animation
    setTimeout(() => {
      this.remove();
    }, 300);

    this.emit('close');
  }

  teardown(): void {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
  }
}

customElements.define('ry-toast', RyToast);
